import { FormikHelpers, setIn } from 'formik';
import { TFunction } from 'i18next';

import { CPM_METADATA_FIELD_MAP } from 'api/cashierPaymentMethods/normalizers';
import {
  getFlatFormErrors,
  getFormErrors,
  getFormTouched,
  joinErrorMessages,
} from 'utils/common';
import { IAllPageFilters, INullable, TSearch } from 'utils/types';

import { METADATA_KEY_VALUES } from './constants';
import { IMetadata, IMetadataPayload } from './types';

export const isInOperator = (operation: INullable<string>): boolean =>
  operation === METADATA_KEY_VALUES.IN || operation === METADATA_KEY_VALUES.NOT_IN;

// Different grids expose the merchant / terminal filter keys differently:
//   * Routing Ruleset grid uses nested paths (`merchantTerminal.merchant.id`,
//     `merchantTerminal.name`, …) because rulesets are nested under terminals.
//   * Cashier Payment Method grid exposes the same data at flat top-level keys
//     (`merchantId`, `merchantName`, `merchantTerminalId`, `merchantTerminalName`)
//     because the CPM entity already lives at the row's top level.
// Both maps remap onto the metadata-templates endpoint's contract
// (`merchant.id`, `merchant.name`, `merchantTerminal.id`, `merchantTerminal.name`)
// so this util can be used from either grid without the caller knowing the shape.
const MERCHANT_AND_ORG_FILTER_KEYS: Record<string, string> = {
  'merchant.id': 'merchant.id',
  'merchant.name': 'merchant.name',
  merchantId: 'merchant.id',
  merchantName: 'merchant.name',
  'merchantTerminal.id': 'merchantTerminal.id',
  'merchantTerminal.merchant.id': 'merchant.id',
  'merchantTerminal.merchant.name': 'merchant.name',
  'merchantTerminal.name': 'merchantTerminal.name',
  merchantTerminalId: 'merchantTerminal.id',
  merchantTerminalName: 'merchantTerminal.name',
};

export const pickMerchantAndOrgFilters = (
  filters: IAllPageFilters | undefined,
): IAllPageFilters | undefined => {
  const sourceSearch = filters?.search;
  if (!sourceSearch) {
    return undefined;
  }
  const search: TSearch = {};
  Object.entries(MERCHANT_AND_ORG_FILTER_KEYS).forEach(([sourceKey, targetKey]) => {
    const value = sourceSearch[sourceKey];
    if (value !== undefined && search[targetKey] === undefined) {
      search[targetKey] = value;
    }
  });
  return Object.keys(search).length ? { search } : undefined;
};

export const EMPTY_METADATA_PAYLOAD: IMetadataPayload = {
  equalsValues: null,
  exist: null,
  inValues: null,
  notEqualsValues: null,
  notExist: null,
  notInValues: null,
};

export const normalizeMetadataPayload = (
  data: INullable<IMetadata[]>,
): INullable<IMetadataPayload> => {
  const result: IMetadataPayload = { ...EMPTY_METADATA_PAYLOAD };

  let hasValidEntry = false;

  data
    ?.filter((item) => !!item.key)
    .forEach(({ key, operation, value }) => {
      const isExistsOperators =
        operation === METADATA_KEY_VALUES.EXIST ||
        operation === METADATA_KEY_VALUES.NOT_EXIST;
      const isEqualsOperators =
        operation === METADATA_KEY_VALUES.EQUALS ||
        operation === METADATA_KEY_VALUES.NOT_EQUALS;
      const isInOperators =
        operation === METADATA_KEY_VALUES.IN || operation === METADATA_KEY_VALUES.NOT_IN;

      if (isExistsOperators) {
        result[operation] = result[operation] ? `${result[operation]},${key}` : key;
        hasValidEntry = true;
      } else if (isEqualsOperators && !!value) {
        const pair = `${key}:${value}`;
        result[operation] = result[operation] ? `${result[operation]},${pair}` : pair;
        hasValidEntry = true;
      } else if (isInOperators) {
        const values = (value as string[])?.filter((item) => !!item);
        if (values?.length) {
          const pair = `${key}:${values.join('|')}`;
          result[operation] = result[operation] ? `${result[operation]},${pair}` : pair;
          hasValidEntry = true;
        }
      }
    });

  return hasValidEntry ? result : null;
};

// Reverse of CPM_METADATA_FIELD_MAP — maps the API's flat metadata field names
// back onto the row-editor operator keys (`metadataEqualsValues` -> `equalsValues`).
const CPM_METADATA_ERROR_FIELD_MAP = Object.fromEntries(
  (Object.entries(CPM_METADATA_FIELD_MAP) as [keyof IMetadataPayload, string][]).map(
    ([operation, field]) => [field, operation],
  ),
) as Record<string, keyof IMetadataPayload>;

// The cashier-payment-method API returns metadata validation errors keyed by the
// same flat field names it accepts in the payload (`metadataEqualsValues`, …).
// `mapServerErrorsForForm` matches the dotted `metadata.<operation>` shape it
// shares with Routing Ruleset, so rewrite CPM's flat keys onto that shape before
// handing the errors off. Non-metadata keys pass through untouched.
export const remapCpmMetadataErrorKeys = (
  errors: Record<string, string | string[]>,
): Record<string, string | string[]> => {
  const result: Record<string, string | string[]> = {};

  Object.entries(errors).forEach(([key, value]) => {
    const operation = CPM_METADATA_ERROR_FIELD_MAP[key];
    result[operation ? `metadata.${operation}` : key] = value;
  });

  return result;
};

// The server returns metadata validation errors aggregated per operation, e.g.
//   { "metadata.equalsValues": ["error.validation.mustBeUnique"] }
// (or "transaction.metadata.<op>" on the ruleset side). The response identifies
// only the operation — never the offending row — so the message is returned keyed
// by operation, letting callers pinpoint the conflicting rows. Non-metadata keys
// pass through as `remainingErrors`.
const METADATA_ERROR_KEY_PREFIX = /^(?:transaction\.)?metadata(?:\.|$)/;
const METADATA_ERROR_OPERATION_KEY = /^(?:transaction\.)?metadata\.([^.]+)$/;

export interface IMappedMetadataServerErrors {
  metadataOperationMessages: Record<string, string>;
  metadataSectionMessages: string[];
  remainingErrors: Record<string, string | string[]>;
}

export interface IMetadataRowError {
  key?: string;
  value?: string | string[];
}

export const mapServerErrorsForForm = ({
  errors,
  t,
}: {
  errors: Record<string, string | string[]>;
  t: TFunction;
}): IMappedMetadataServerErrors => {
  const metadataOperationMessages: Record<string, string> = {};
  const metadataSectionMessages: string[] = [];
  const remainingErrors: Record<string, string | string[]> = {};

  Object.entries(errors).forEach(([key, value]) => {
    if (!METADATA_ERROR_KEY_PREFIX.test(key)) {
      remainingErrors[key] = value;
      return;
    }

    const errorsList = Array.isArray(value) ? value : [value];
    const message = joinErrorMessages(
      errorsList.map((errorMsg) => {
        const [intlKey, from, to, unit] = errorMsg.split(':');
        return t(intlKey as never, { from, to, unit } as never);
      }),
    );

    metadataSectionMessages.push(message);

    const operation = key.match(METADATA_ERROR_OPERATION_KEY)?.[1];
    if (operation) {
      metadataOperationMessages[operation] = message;
    }
  });

  return { metadataOperationMessages, metadataSectionMessages, remainingErrors };
};

// Without `fieldName` the form keeps flat field names and metadata at `metadata`
// (cashier payment methods); with it, errors and touched are nested like the
// forms that mount metadata under a path such as `transaction.metadata`.
export const applyMetadataServerErrors = <Values>({
  errors,
  fieldName,
  helpers,
  metadata,
  t,
}: {
  errors: Record<string, string | string[]>;
  fieldName?: string;
  helpers: FormikHelpers<Values>;
  metadata: INullable<IMetadata[]>;
  t: TFunction;
}): void => {
  const { metadataOperationMessages, metadataSectionMessages, remainingErrors } =
    mapServerErrorsForForm({ errors, t });

  const metadataPath = fieldName ?? 'metadata';
  const fieldErrors = fieldName
    ? getFormErrors({ errors: remainingErrors, getTranslation: t })
    : getFlatFormErrors({ errors: remainingErrors, getTranslation: t });
  const rejectedOperations = Object.keys(metadataOperationMessages);

  const keyCounts = new Map<string, number>();
  if (Array.isArray(metadata)) {
    metadata.forEach((row) => {
      if (row?.key && row.operation && rejectedOperations.includes(row.operation)) {
        keyCounts.set(row.key, (keyCounts.get(row.key) ?? 0) + 1);
      }
    });
  }

  const hasDuplicateKeys = [...keyCounts.values()].some((count) => count >= 2);

  const rowErrors: IMetadataRowError[] = [];
  if (Array.isArray(metadata)) {
    metadata.forEach((row, index) => {
      if (!row?.key || !row.operation || !rejectedOperations.includes(row.operation)) {
        return;
      }
      // With duplicate keys present, flag only the duplicates; otherwise the whole
      // operator was rejected, so flag every row that uses a rejected operator.
      if (hasDuplicateKeys && (keyCounts.get(row.key) ?? 0) < 2) {
        return;
      }
      const message = metadataOperationMessages[row.operation];
      rowErrors[index] = {
        key: message,
        value: Array.isArray(row.value) ? row.value.map(() => message) : message,
      };
    });
  }

  const hasRowErrors = rowErrors.length > 0;
  const sectionMessage = [...new Set(metadataSectionMessages)].join('; ');

  let metadataError: IMetadataRowError[] | string | undefined;
  if (hasRowErrors) {
    metadataError = rowErrors;
  } else if (sectionMessage) {
    metadataError = sectionMessage;
  }

  helpers.setErrors(
    (metadataError !== undefined
      ? setIn(fieldErrors, metadataPath, metadataError)
      : fieldErrors) as never,
  );

  // setTouched replaces the whole touched tree, so it must run before the row flags.
  if (fieldName) {
    helpers.setTouched(getFormTouched(remainingErrors) as never, false);
  }

  if (!hasRowErrors) {
    return;
  }

  rowErrors.forEach((rowError, index) => {
    if (!rowError) return;
    helpers.setFieldTouched(`${metadataPath}[${index}].key`, true, false);
    if (Array.isArray(rowError.value)) {
      rowError.value.forEach((_, valueIndex) => {
        helpers.setFieldTouched(
          `${metadataPath}[${index}].value[${valueIndex}]`,
          true,
          false,
        );
      });
    } else {
      helpers.setFieldTouched(`${metadataPath}[${index}].value`, true, false);
    }
  });
};

// Parse a server-side `IMetadataPayload` (six comma-joined `key:value` /
// `key:v1|v2` strings, one per operator) back into the form-side
// `IMetadata[]` shape used by the row editor. Used when seeding form
// initial values from a detail endpoint response.
export const parseMetadataPayload = (
  payload: INullable<Partial<IMetadataPayload>> | undefined,
): IMetadata[] => {
  if (!payload) {
    return [];
  }

  const rows: IMetadata[] = [];

  const pushExistsRow = (operation: METADATA_KEY_VALUES, raw: INullable<string>) => {
    if (!raw) return;
    raw
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean)
      .forEach((key) => {
        rows.push({ key, operation, useTemplate: false, value: null });
      });
  };

  const pushEqualsRow = (operation: METADATA_KEY_VALUES, raw: INullable<string>) => {
    if (!raw) return;
    raw
      .split(',')
      .map((pair) => pair.trim())
      .filter(Boolean)
      .forEach((pair) => {
        const separatorIndex = pair.indexOf(':');
        if (separatorIndex === -1) return;
        const key = pair.slice(0, separatorIndex).trim();
        const value = pair.slice(separatorIndex + 1).trim();
        if (!key) return;
        rows.push({ key, operation, useTemplate: false, value });
      });
  };

  const pushInRow = (operation: METADATA_KEY_VALUES, raw: INullable<string>) => {
    if (!raw) return;
    raw
      .split(',')
      .map((pair) => pair.trim())
      .filter(Boolean)
      .forEach((pair) => {
        const separatorIndex = pair.indexOf(':');
        if (separatorIndex === -1) return;
        const key = pair.slice(0, separatorIndex).trim();
        const values = pair
          .slice(separatorIndex + 1)
          .split('|')
          .map((v) => v.trim())
          .filter(Boolean);
        if (!key || values.length === 0) return;
        rows.push({ key, operation, useTemplate: false, value: values });
      });
  };

  pushExistsRow(METADATA_KEY_VALUES.EXIST, payload.exist ?? null);
  pushExistsRow(METADATA_KEY_VALUES.NOT_EXIST, payload.notExist ?? null);
  pushEqualsRow(METADATA_KEY_VALUES.EQUALS, payload.equalsValues ?? null);
  pushEqualsRow(METADATA_KEY_VALUES.NOT_EQUALS, payload.notEqualsValues ?? null);
  pushInRow(METADATA_KEY_VALUES.IN, payload.inValues ?? null);
  pushInRow(METADATA_KEY_VALUES.NOT_IN, payload.notInValues ?? null);

  return rows;
};

// Build the metadata payload for group edit so that only operators the user
// actually touched in this session are sent — operators with no row stay out
// of the payload entirely, so previously saved conditions on those operators
// are preserved server-side. Toggle-on-with-no-rows still wipes everything.
export const normalizeGroupEditMetadataPayload = (
  data: INullable<IMetadata[]>,
): Partial<IMetadataPayload> => {
  if (!data || data.length === 0) {
    return { ...EMPTY_METADATA_PAYLOAD };
  }

  const normalized = normalizeMetadataPayload(data);
  const touchedOperations = new Set<keyof IMetadataPayload>();

  data.forEach((row) => {
    if (row.operation) {
      touchedOperations.add(row.operation as keyof IMetadataPayload);
    }
  });

  const result: Partial<IMetadataPayload> = {};
  touchedOperations.forEach((operation) => {
    result[operation] = normalized?.[operation] ?? null;
  });

  return result;
};
